import { Link } from 'react-router-dom';
import { ContactLink } from '@/components/contact';

/**
 * Security Page
 *
 * Explains FlowIO's security features in user-friendly language.
 * This is a public page accessible without authentication.
 *
 * WP-CONTACT-1: Updated to use ContactLink instead of mailto
 */
export function SecurityPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-primary-600">
              FlowIO
            </Link>
            <nav className="flex gap-4">
              <Link
                to="/login"
                className="text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Get Started
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <article className="bg-white rounded-lg shadow-sm p-8 sm:p-12">
          {/* Title */}
          <header className="mb-10 pb-8 border-b border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Security at FlowIO
            </h1>
            <p className="text-gray-600">
              Your privacy is our number one priority—and we built FlowIO to prove it.
            </p>
          </header>

          {/* Security Features */}
          <div className="space-y-10">
            {/* Privacy by Design */}
            <section>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Privacy by Design
                  </h2>
                  <p className="text-gray-600 mb-2">
                    <span className="font-medium">Philosophy:</span> Collect less, protect more
                  </p>
                  <p className="text-gray-700 mb-3">
                    FlowIO empowers you to take charge of your finances. To do that, we need
                    to see your transactions—but remarkably little else.
                  </p>
                  <p className="text-gray-700 mb-3">
                    <span className="font-medium">What we never see or store:</span>
                  </p>
                  <ul className="text-gray-700 mb-3 list-disc list-inside space-y-1">
                    <li>Your bank login credentials (you authenticate directly with your bank)</li>
                    <li>Your full account numbers (only the last 4 digits)</li>
                    <li>Your home address or physical location</li>
                    <li>Any path to move money or execute transactions</li>
                  </ul>
                  <p className="text-gray-700 mb-3">
                    We even encourage you to use a display name instead of your real name.
                    If your account were ever compromised, attackers would not learn your
                    legal identity.
                  </p>
                  <p className="text-gray-700">
                    The best way we can protect your data is to never have it in the first place.
                  </p>
                </div>
              </div>

              {/* Privacy Isolation Video */}
              <div className="mt-6 ml-16">
                <div className="relative rounded-lg overflow-hidden bg-slate-900 shadow-lg">
                  <video
                    className="w-full aspect-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster="/videos/security-explainer-poster.jpg"
                  >
                    <source src="/videos/security-explainer.mp4?v=3" type="video/mp4" />
                    <source src="/videos/security-explainer.webm?v=3" type="video/webm" />
                    {/* Fallback for browsers that don't support video */}
                    <div className="flex items-center justify-center h-full bg-slate-900 text-slate-400 p-8">
                      <p className="text-center">
                        Your browser doesn&apos;t support video playback.
                        FlowIO uses a secure intermediary to access your bank data
                        without ever seeing your credentials.
                      </p>
                    </div>
                  </video>
                  {/* Subtle caption */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                    <p className="text-xs text-white/70 text-center">
                      Data flows one way. Your money stays protected.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Bank Connection Security */}
            <section>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Bank Connection Security
                  </h2>
                  <p className="text-gray-600 mb-2">
                    <span className="font-medium">Technical:</span> Plaid/Akoya aggregation
                  </p>
                  <p className="text-gray-700">
                    We never see your bank login credentials. Plaid and Akoya act as trusted
                    intermediaries - you authenticate directly with them, and they provide us
                    only the transaction data we need. Your bank username and password never
                    touch our servers.
                  </p>
                </div>
              </div>
            </section>

            {/* Password Protection */}
            <section>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Password Protection
                  </h2>
                  <p className="text-gray-600 mb-2">
                    <span className="font-medium">Technical:</span> Argon2id hashing
                  </p>
                  <p className="text-gray-700">
                    Your password is never stored. We keep only a mathematical fingerprint
                    that cannot be reversed. Even if our database were breached, attackers
                    could not recover your password.
                  </p>
                </div>
              </div>
            </section>

            {/* Multi-Factor Authentication */}
            <section>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Multi-Factor Authentication
                  </h2>
                  <p className="text-gray-600 mb-2">
                    <span className="font-medium">Technical:</span> TOTP, SMS, Passkeys, Recovery codes
                  </p>
                  <p className="text-gray-700">
                    Even if someone gets your password, they cannot log in without your phone
                    or security key. We support authenticator apps, text messages, and hardware
                    security keys.
                  </p>
                </div>
              </div>
            </section>

            {/* Data Encryption */}
            <section>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Data Encryption
                  </h2>
                  <p className="text-gray-600 mb-2">
                    <span className="font-medium">Technical:</span> AES-256-GCM at rest
                  </p>
                  <p className="text-gray-700">
                    Your sensitive data (like bank access tokens) is scrambled using
                    military-grade encryption. Even direct database access would not reveal
                    the actual values.
                  </p>
                </div>
              </div>
            </section>

            {/* Session Security */}
            <section>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Session Security
                  </h2>
                  <p className="text-gray-600 mb-2">
                    <span className="font-medium">Technical:</span> 15-min access tokens, HttpOnly cookies
                  </p>
                  <p className="text-gray-700">
                    Your login session expires quickly and cannot be stolen by malicious
                    scripts. We use short-lived tokens that refresh automatically while
                    you are active.
                  </p>
                </div>
              </div>
            </section>

            {/* Device Trust */}
            <section>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-pink-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Device Trust
                  </h2>
                  <p className="text-gray-600 mb-2">
                    <span className="font-medium">Technical:</span> Fingerprinting, new device alerts
                  </p>
                  <p className="text-gray-700">
                    We recognize your devices. When someone logs in from a new device, you
                    will be alerted. You can review and revoke trusted devices at any time.
                  </p>
                </div>
              </div>
            </section>

            {/* Account Protection */}
            <section>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Account Protection
                  </h2>
                  <p className="text-gray-600 mb-2">
                    <span className="font-medium">Technical:</span> 5-attempt lockout, rate limiting
                  </p>
                  <p className="text-gray-700">
                    Automated attacks are blocked. After a few failed password attempts,
                    the account is temporarily locked. We also limit how fast anyone can
                    try to log in.
                  </p>
                </div>
              </div>
            </section>

            {/* Data Isolation */}
            <section>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Data Isolation
                  </h2>
                  <p className="text-gray-600 mb-2">
                    <span className="font-medium">Technical:</span> Multi-tenant isolation
                  </p>
                  <p className="text-gray-700">
                    Your financial data is completely separated from other users at the
                    database level. Every query is scoped to your household - there is no
                    way to accidentally access another user&apos;s data.
                  </p>
                </div>
              </div>
            </section>

            {/* Secure Connections */}
            <section>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-cyan-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    Secure Connections
                  </h2>
                  <p className="text-gray-600 mb-2">
                    <span className="font-medium">Technical:</span> HTTPS/TLS 1.3, HSTS
                  </p>
                  <p className="text-gray-700">
                    All data between your browser and our servers is encrypted in transit.
                    We enforce secure connections and prevent downgrade attacks.
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* Questions Section */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Questions About Security?
            </h2>
            <p className="text-gray-700 mb-4">
              If you have questions about our security practices or want to report a
              security concern, please{' '}
              <ContactLink
                topic="security"
                context="SecurityPage - Security questions or concerns"
                className="text-primary-600 hover:underline cursor-pointer"
              >
                contact our security team
              </ContactLink>.
            </p>
          </div>
        </article>

        {/* Related Links */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <Link to="/privacy" className="hover:text-gray-700 hover:underline">
            Privacy Policy
          </Link>
          <span className="mx-3">|</span>
          <Link to="/terms" className="hover:text-gray-700 hover:underline">
            Terms of Service
          </Link>
          <span className="mx-3">|</span>
          <Link to="/" className="hover:text-gray-700 hover:underline">
            Back to Home
          </Link>
        </div>
      </main>

      {/* Page Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500">
            Copyright {new Date().getFullYear()} FlowIO. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default SecurityPage;
